import { routerRedux } from 'dva/router';
import { addLocaleData } from 'react-intl';
import queryString from 'query-string';
import en from 'react-intl/locale-data/en';
import de from 'react-intl/locale-data/de';
import { notification } from 'antd';
import pathToRegexp from 'path-to-regexp';
import messages from '../i18n';
import config from '../config';
import * as AR from '../services/app';
import { helpers } from '../utils';

addLocaleData([...en, ...de]);

export const ACTION_APP_GO_TO_ROUTE = 'app/goToRoute';
export const ACTION_APP_SHOW_NOTIFICATION = 'app/showNotifications';
export const ACTION_APP_ADD_NOTIFICATION = 'app/addNotification';

const defaultState = {
  messages,
  locale: 'en',
  user: {
    first_name: '',
    last_name: '',
    locale: '',
    email: '',
    avatar: '',
    display_datetime_format: 'DD.MM.YYYY HH:mm',
    display_timezone: 'Europe/Berlin',
    hubspot_permissions: [-1, -2],
  },
  projects: [],
  firstVisit: true,
  token: window.localStorage.getItem('access_token'),
  auth: false,
  currentProjectId: 0, // deprecated, please use currentProject.id
  currentProject: {},
  roles: [],
  notifications: [],
  firstLogin: {
    email: '',
    token: '',
    validToken: null,
  },
  useCustomBranches: false,
  matomoProjectId: 0,
  header: {
    title: null,
    description: null,
  },
};

export default {
  namespace: 'app',

  state: defaultState,

  reducers: {
    updateStore(state, { payload }) {
      return {
        ...state,
        ...payload,
      };
    },

    updateToken(state, { payload: { token } }) {
      window.localStorage.setItem('access_token', token);
      return {
        ...state,
        token,
      };
    },
  },

  subscriptions: {
    setup({ dispatch }) {
      const parsed = queryString.parseUrl(window.location.pathname + window.location.hash);
      const { email, token, locale } = parsed.query;
      const path = parsed.url.substr(0, parsed.url.length - 1);
      const recoverPassword = path === config['app.frontend_forget_password_url'];

      if (config['app.system.branch_type'] === 'CUSTOM') {
        dispatch({ type: 'updateStore', payload: { useCustomBranches: true } });
      }

      if (locale) {
        const payload = { locale };
        if (recoverPassword) {
          payload.firstVisit = false;
        }
        dispatch({
          type: 'updateStore',
          payload,
        });
      }

      if ((recoverPassword || path === config['app.frontend_change_password_url']) && token && email) {
        window.localStorage.setItem('access_token', false);
        dispatch({
          type: 'tokenValidator',
          payload: { email, token },
        });
      } else if (window.localStorage.getItem('access_token')) {
        dispatch({ type: 'ping' });
      } else {
        dispatch({ type: 'logout' });
      }
    },

    setupHistory({ dispatch, history }) {
      history.listen((location) => {
        dispatch({
          type: 'updateStore',
          payload: { location },
        });
      });
    },

  },
  effects: {
    * ping({ payload }, { call, put, select }) {
      try {
        const data = yield call(AR.pingRequest, payload);
        if (data.success) {
          yield put({
            type: 'authenticated',
            payload: {
              auth: true,
            },
          });

          const { location } = yield select(store => store.app);
          if (location.pathname === '/login/') {
            yield put(routerRedux.push('/'));
          } else if (location.pathname === '/logout/') {
            yield put({ type: 'logout' });
          }
        } else {
          yield put({ type: 'logout' });
        }
      } catch (e) {
        helpers.processException(e);
        throw new Error(e.message);
      }
    },

    * authenticated({ payload: { auth } }, { put }) {
      yield put({ type: 'updateStore', payload: { auth } });
      if (auth) {
        yield put({ type: 'start' });
      }
    },

    * start(options, { put }) {
      yield put.resolve({ type: 'loadRoles' });
      yield put.resolve({ type: 'loadCurrentUserData' });

      // load default project
      yield put.resolve({ type: 'selectProject', payload: { init: true } });
    },

    * selectProject({ payload = {} }, { select, put }) {
      const { projects, location } = yield select(store => store.app);
      const defaultProjectId = helpers.getDefaultProjectId(projects);
      const urlParams = pathToRegexp('/:projectId/(.*)').exec(location.pathname);
      // const [, projectIdFromUrl, restOfUrl] = urlParams || [];
      const [, projectIdFromUrl] = urlParams || [];
      const pId = Number(payload.projectId || projectIdFromUrl);

      const projectFromUrl = projects.find(p => p.id === pId);
      const defaultProject = projects.find(p => p.id === defaultProjectId);

      if (pId && projectFromUrl) {
        yield put({ type: 'updateStore', payload: { currentProjectId: pId, currentProject: projectFromUrl } });
        // yield put(routerRedux.replace(`/${pId}/${restOfUrl}`));
        // if not initial loading page
        if (!payload.init) {
          // navigates to visits grid to hide latin text on Home page
          yield put({
            type: 'goToRoute',
            payload: {
              routeName: 'LEAD_VISITS_LIST',
              projectId: pId,
            },
          });
        }

        return pId;
      } else if (defaultProjectId && (pId || location.pathname === '/')) {
        yield put({ type: 'updateStore', payload: { currentProjectId: defaultProjectId, currentProject: defaultProject } });
        // yield put(routerRedux.replace(`/${defaultProjectId}/`));

        // navigates to visits grid to hide latin text on Home page
        yield put({ type: 'goToRoute', payload: { routeName: 'LEAD_VISITS_LIST', projectId: defaultProjectId } });
        return defaultProjectId;
      } else {
        // not a number in url and no default project means there is no assigned projects to user
        yield put({ type: 'updateStore', payload: { currentProjectId: defaultProjectId, currentProject: defaultProject } });
        return defaultProjectId;
      }
    },

    // eslint-disable-next-line no-unused-vars
    * loadRoles({ payload = {} }, { call, put }) {
      const { success, data } = yield call(AR.rolesRequest);
      if (success) {
        yield put({ type: 'updateRoles', payload: { roles: data } });
      }
    },

    * updateRoles({ payload: { roles } }, { put }) {
      yield put({ type: 'updateStore', payload: { roles } });
    },

    // eslint-disable-next-line no-unused-vars
    * loadCurrentUserData({ payload }, { put, call }) {
      try {
        const currentUserDataResponse = yield call(AR.currentUserDataRequest);

        if (currentUserDataResponse.success) {
          const { locale } = currentUserDataResponse;

          let projectsResponse = {};
          let projects = currentUserDataResponse.projects;

          if (currentUserDataResponse.super_admin) {
            projectsResponse = yield call(AR.projectsRequest);
            projects = projectsResponse.data;
          }

          const payloadResult = {
            locale,
            projects,
            user: {
              ...currentUserDataResponse.body,
              hubspot_permissions: currentUserDataResponse.hubspot_permissions.map(i => i.project_id),
            },
          };

          yield put({
            type: 'updateCurrentUserData',
            payload: payloadResult,
          });
        }
      } catch (e) {
        helpers.processException(e);
        return Promise.reject();
      }
    },

    * updateCurrentUserData({ payload }, { put }) {
      yield put({ type: 'updateStore', payload });
    },

    * waitUntilAppDataLoaded(p, { select, take }) {
      const { currentProjectId } = yield select(store => store.app);
      if (!currentProjectId) {
        // if no project selected yet, then wait until selected and proceed
        yield take('start/@@end');
      }
    },

    // eslint-disable-next-line no-unused-vars
    * showNotifications({ payload }, { select, put }) {
      const { notifications } = yield select(store => store.app);
      notifications.forEach((item) => {
        const { type } = item;
        notification[type](item);
      });

      if (notifications.length) {
        yield put({ type: 'clearAllNotifications' });
      }
    },

    * addNotification({ payload }, { select, put }) {
      const { locale, notifications } = yield select(store => store.app);
      yield put({
        type: 'updateStore',
        payload: {
          notifications: [...notifications, {
            ...payload,
            message: messages[locale][payload.message],
            description: messages[locale][payload.description],
          }],
        },
      });
    },

    * addAndShowNotification({ payload }, { put }) {
      yield put({ type: 'addNotification', payload });
      yield put({ type: 'showNotifications' });
    },

    // eslint-disable-next-line no-unused-vars
    * clearAllNotifications({ payload }, { put }) {
      yield put({ type: 'updateStore', payload: { notifications: [] } });
    },

    * goToRoute({ payload }, { put }) {
      const { routeName, method = 'push', ...params } = payload;
      const returnPath = helpers.compileUrl(routeName, params);
      yield put(routerRedux[method](returnPath));
    },

    // eslint-disable-next-line no-unused-vars
    * logout({ payload }, { put }) {
      if (window.localStorage.getItem('access_token')) {
        helpers.forceLogout();
      } else {
        yield put({
          type: 'updateStore',
          payload: {
            ...defaultState,
          },
        });
        yield put({ type: 'goToRoute', payload: { routeName: 'LOGIN' } });
      }

      return Promise.reject();
    },

    * forgotPassword({ payload }, { call }) {
      try {
        return yield call(AR.forgotPasswordRequest, payload);
      } catch (e) {
        return Promise.reject();
      }
    },

    * tokenValidator({ payload }, { put, call }) {
      const { token, email } = payload;
      try {
        const data = yield call(AR.tokenValidationRequest, payload, 'tokenRequest');
        if (data.success) {
          yield put({ type: 'updateStore', payload: { firstLogin: { validToken: true, token, email } } });
          yield put({ type: 'goToRoute', payload: { routeName: 'FIRST_LOGIN', token, email } });
        }
      } catch (e) {
        yield put({
          type: 'updateStore',
          payload: { firstLogin: { validToken: false } },
        });
        yield put({ type: 'goToRoute', payload: { routeName: 'FIRST_LOGIN' } });
      }
    },

    * changePassword({ payload }, { call }) {
      return yield call(AR.tokenValidationRequest, payload);
    },
  },
};



// WEBPACK FOOTER //
// ./src/models/app.js